import React, {useContext, useEffect, useState} from 'react';
import {Icon, Layout, Menu} from 'antd';
import MobileServiceNavigation from './MobileServiceNavigation';
import {Link, Route, useHistory, useLocation} from 'react-router-dom';

import {AppContext} from '../../../contexts/app-context';
import GuestInstructionAccessGate from './GuestInstructionAccessGate';
import {shouldBlurInstruction} from '../../../presentation/service-instructions/instruction-guest-visibility-policy';

import './style.css';
import Instruction from './instruction';
import Support from './support';
import Edo from './edo';
import NotificationsInstruction from './notifications';
import TimeWindowsInstruction from './time-windows';
import LegalEntityDisagreementInstruction from './legal-entity-disagreement';
import VehicleInspectionInstruction from './vehicle-inspection';
import VatInstruction from './vat';
import PartnersInstruction from './partners';
import GosLogVitalApprovalInstruction from './goslog-vital-approval';
import ContractTerminationInstruction from './contract-termination';
import BidSubmissionInstruction from './bid-submission';
import i18n from '../../../i18n';


function MenuLabel({icon, title}) {
    return (
        <span>
            <Icon type={icon}/>
            <span>{title}</span>
        </span>
    );
}


function createMenuElement(key, link, title, icon) {
    return {
        key,
        link,
        sortTitle: title,
        label: (
            <MenuLabel
                icon={icon}
                title={title}
            />
        ),
        subMenu: false,
        items: []
    };
}


function LeftMenu({menuElementsData}) {
    const history = useHistory();
    const location = useLocation();

    const isActivePath = (link) => (
        location.pathname === link ||
        location.pathname.startsWith(`${link}/`)
    );

    const activeSubMenu = menuElementsData.find((menuItemData) => (
        menuItemData.subMenu &&
        (
            location.pathname === menuItemData.link ||
            menuItemData.items.some(
                (item) => isActivePath(item.link)
            )
        )
    ));

    const activeSubMenuKey = activeSubMenu
        ? activeSubMenu.key
        : null;

    const [openKeys, setOpenKeys] = useState(
        activeSubMenuKey ? [activeSubMenuKey] : []
    );

    useEffect(() => {
        setOpenKeys(
            activeSubMenuKey ? [activeSubMenuKey] : []
        );
    }, [activeSubMenuKey]);

    let selectedKey = null;

    menuElementsData.some((menuItemData) => {
        if (menuItemData.subMenu) {
            const selectedChild = menuItemData.items.find(
                (item) => isActivePath(item.link)
            );

            if (selectedChild) {
                selectedKey = selectedChild.key;

                return true;
            }

            if (location.pathname === menuItemData.link) {
                selectedKey = menuItemData.key;

                return true;
            }

            return false;
        }

        if (isActivePath(menuItemData.link)) {
            selectedKey = menuItemData.key;

            return true;
        }

        return false;
    });

    const handleOpenChange = (nextOpenKeys) => {
        const shouldKeepSubMenuOpen =
            activeSubMenu &&
            location.pathname !== activeSubMenu.link &&
            !nextOpenKeys.includes(activeSubMenu.key);

        if (shouldKeepSubMenuOpen) {
            setOpenKeys([activeSubMenu.key]);

            return;
        }

        setOpenKeys(nextOpenKeys);
    };

    const menuElements = menuElementsData.map((menuItemData) => {
        if (menuItemData.subMenu) {
            const groupItems = menuItemData.items.map((item) => (
                <Menu.Item key={item.key}>
                    <Link to={item.link}>
                        {item.label}
                    </Link>
                </Menu.Item>
            ));

            return (
                <Menu.SubMenu
                    className={
                        location.pathname === menuItemData.link
                            ? 'service-menu__submenu--selected'
                            : ''
                    }
                    key={menuItemData.key}
                    title={menuItemData.label}
                    onTitleClick={() => {
                        if (location.pathname !== menuItemData.link) {
                            history.push(menuItemData.link);
                        }
                    }}
                >
                    {groupItems}
                </Menu.SubMenu>
            );
        }

        return (
            <Menu.Item key={menuItemData.key}>
                <Link to={menuItemData.link}>
                    {menuItemData.label}
                </Link>
            </Menu.Item>
        );
    });

    return (
        <Menu
            selectedKeys={selectedKey ? [selectedKey] : []}
            openKeys={openKeys}
            onOpenChange={handleOpenChange}
            mode="inline"
            theme="light"
        >
            {menuElements}
        </Menu>
    );
}


export default function ServicePage() {
    const [isMobile, setIsMobile] = useState(false);
    const appContext = useContext(AppContext);

    const location = useLocation();

    const isAuthenticated = Boolean(
        appContext.user &&
        appContext.user.loggedIn
    );

    const isInstructionLocked =
        shouldBlurInstruction({
            pathname: location.pathname,
            isAuthenticated
        });

    const instructionMenuTitle = i18n.t(
        'servicePage.menu.instruction'
    );

    const supportMenuTitle = i18n.t(
        'servicePage.menu.support'
    );

    const instructionMenuItems = [
        createMenuElement(
            'instruction-edo',
            '/service/edo',
            i18n.t('servicePage.menu.edo'),
            'file-text'
        ),
        createMenuElement(
            'instruction-notifications',
            '/service/notifications',
            i18n.t('servicePage.menu.notifications'),
            'bell'
        ),
        createMenuElement(
            'instruction-time-windows',
            '/service/time-windows',
            i18n.t('servicePage.menu.timeWindows'),
            'clock-circle'
        ),
        createMenuElement(
            'instruction-legal-entity-disagreement',
            '/service/legal-entity-disagreement',
            i18n.t(
                'servicePage.menu.legalEntityDisagreement'
            ),
            'solution'
        ),
        createMenuElement(
            'instruction-vehicle-inspection',
            '/service/vehicle-inspection',
            i18n.t('servicePage.menu.vehicleInspection'),
            'camera'
        ),
        createMenuElement(
            'instruction-vat',
            '/service/vat',
            i18n.t('servicePage.menu.vat'),
            'calculator'
        ),
        createMenuElement(
            'instruction-partners',
            '/service/partners',
            i18n.t('servicePage.menu.partners'),
            'team'
        ),
        createMenuElement(
            'instruction-contract-termination',
            '/service/contract-termination',
            'Расторжение договора',
            'close-circle'
        ),
        createMenuElement(
            'instruction-goslog-vital-approval',
            '/service/goslog-vital-approval',
            i18n.t('servicePage.menu.gosLogVitalApproval'),
            'audit'
        ),
        createMenuElement(
            'instruction-bid-submission',
            '/service/bid-submission',
            'Подача ставок',
            'swap'
        ),
    ];

    instructionMenuItems.sort((firstItem, secondItem) => (
        firstItem.sortTitle.localeCompare(
            secondItem.sortTitle,
            i18n.language || 'ru',
            {
                sensitivity: 'base'
            }
        )
    ));

    const menuElements = [
        {
            key: 'group1',
            link: '/service/instruction',
            sortTitle: instructionMenuTitle,
            label: (
                <MenuLabel
                    icon="read"
                    title={instructionMenuTitle}
                />
            ),
            subMenu: true,
            items: instructionMenuItems
        },
        createMenuElement(
            'group2',
            '/service/support',
            supportMenuTitle,
            'customer-service'
        )
    ];

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 1000);
        };

        handleResize();
        window.addEventListener(
            'resize',
            handleResize
        );

        return () => {
            window.removeEventListener(
                'resize',
                handleResize
            );
        };
    }, []);

    useEffect(() => {
        if (appContext.filter.enable) {
            appContext.disableFilter();
        }
    }, [
        appContext.filter.enable,
        appContext.disableFilter
    ]);

    return (
        <Layout
            tagName="section"
            className="service-page-layout"
            style={{
                marginTop: '50px',
                padding: '25px 0',
                background: '#fff'
            }}
        >
            {isMobile ? (
                <MobileServiceNavigation
                    instructionTitle={instructionMenuTitle}
                    supportTitle={supportMenuTitle}
                    instructionItems={instructionMenuItems}
                    instructionSelectorLabel={i18n.t(
                        'servicePage.menu.instructionSelector'
                    )}
                    allInstructionsTitle={i18n.t(
                        'servicePage.menu.allInstructions'
                    )}
                    navigationAriaLabel={i18n.t(
                        'servicePage.menu.mobileNavigationAriaLabel'
                    )}
                />
            ) : (
                <Layout.Sider
                    theme="light"
                    width={256}
                >
                    <LeftMenu
                        menuElementsData={menuElements}
                    />
                </Layout.Sider>
            )}

            <Layout.Content
                style={{
                    padding: '0 24px',
                    minHeight: 350
                }}
                className="table-window service-page__content"
            >
                <GuestInstructionAccessGate
                    locked={isInstructionLocked}
                >
                    <Route
                        path="/service/instruction"
                        exact
                        component={Instruction}
                    />
                    <Route
                        path="/service/support"
                        exact
                        component={Support}
                    />
                    <Route
                        path="/service/notifications"
                        exact
                        component={NotificationsInstruction}
                    />
                    <Route
                        path="/service/time-windows"
                        exact
                        component={TimeWindowsInstruction}
                    />
                    <Route
                        path="/service/legal-entity-disagreement"
                        exact
                        component={
                            LegalEntityDisagreementInstruction
                        }
                    />
                    <Route
                        path="/service/vehicle-inspection"
                        exact
                        component={VehicleInspectionInstruction}
                    />
                    <Route
                        path="/service/vat"
                        exact
                        component={VatInstruction}
                    />
                    <Route
                        path="/service/partners"
                        exact
                        component={PartnersInstruction}
                    />
                    <Route
                        path="/service/contract-termination"
                        exact
                        component={ContractTerminationInstruction}
                    />
                    <Route
                        path="/service/edo"
                        exact
                        component={Edo}
                    />
                    <Route
                        path="/service/goslog-vital-approval"
                        exact
                        component={GosLogVitalApprovalInstruction}
                    />
                    <Route
                        path="/service/bid-submission"
                        exact
                        component={BidSubmissionInstruction}
                    />
                </GuestInstructionAccessGate>
            </Layout.Content>
        </Layout>
    );
}
